import React, { useState, useEffect, useMemo } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { all_routes } from "../../router/all_routes";
import FlightSearch from "../flightSearch";
import FlightFilter from "../flightFilter";
import { usePacote, PacoteDTO } from "../../../core/common/PacoteContext/PacoteContext";

interface Filtros {
  termo?: string;
  idioma?: string;
  pessoas?: number;
  servicoId?: number | null;
  data?: string;
  hora?: string;
  precoRange?: [number, number];
  duracaoRange?: [number, number];
  temDiferencial?: boolean;
  reviews?: number[];
}

const FlightGrid = () => {
  const routes = all_routes;
  const { pacotes: pacotesContext } = usePacote();
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [pacotesOriginais, setPacotesOriginais] = useState<PacoteDTO[]>([]);
  const [resumos, setResumos] = useState<Record<number, { media: number; total: number }>>({});
  const [filtros, setFiltros] = useState<Filtros>({
    termo: "",
    idioma: "",
    pessoas: 1,
    servicoId: null,
    data: "",
    hora: "",
    precoRange: [0, 10000],
    duracaoRange: [0, 1440],
    temDiferencial: false,
    reviews: [],
  });

  const [selectedItems, setSelectedItems] = useState<boolean[]>([]);

  // ✅ Buscar pacotes: usa contexto se tiver, caso contrário faz fetch de todos
  useEffect(() => {
    const fetchPacotes = async () => {
      if (!pacotesContext || pacotesContext.length === 0) {
        try {
          const res = await fetch(`${BASE_URL}/pacotes/search`);
          if (!res.ok) throw new Error("Erro ao buscar pacotes");
          const result: PacoteDTO[] = await res.json();
          console.log("chegou aqui")
          setPacotesOriginais(result);
        } catch (err) {
          console.error(err);
        }
      } else {
        setPacotesOriginais(pacotesContext);
      }
    };

    fetchPacotes();
  }, [BASE_URL, pacotesContext]);

  // ✅ Buscar resumos de avaliações (um por pacote)
  useEffect(() => {
    const fetchResumos = async () => {
      if (pacotesOriginais.length === 0) return;

      try {
        const responses = await Promise.all(
          pacotesOriginais.map((p) =>
            fetch(`${BASE_URL}/avaliacoes/pacote/${p.id}/resumo`).then((res) => res.json())
          )
        );

        const map: Record<number, { media: number; total: number }> = {};
        pacotesOriginais.forEach((p, idx) => {
          map[p.id!] = responses[idx];
        });

        setResumos(map);
      } catch (err) {
        console.error("Erro ao buscar resumos:", err);
      }
    };

    fetchResumos();
  }, [pacotesOriginais, BASE_URL]);


  // Atualiza selectedItems quando pacotesFiltrados mudar
  const pacotesFiltrados = useMemo(() => {
    return pacotesOriginais.filter((p) => {
      // 🔍 Busca por termo no título
      if (filtros.termo && !p.titulo?.toLowerCase().includes(filtros.termo.toLowerCase())) {
        return false;
      }

      if (
        filtros.idioma &&
        !p.idiomas?.some((i) => i.toLowerCase() === filtros.idioma!.toLowerCase())
      ) {
        return false;
      }

      // 🏷️ Filtro por serviço
      if (filtros.servicoId && p.servicoId !== filtros.servicoId) {
        return false;
      }

      // 💰 Filtro por preço
      if (filtros.precoRange) {
        const preco = p.precoBase || 0;
        if (preco < filtros.precoRange[0] || preco > filtros.precoRange[1]) {
          return false;
        }
      }

      // ⏳ Filtro por duração
      if (filtros.duracaoRange) {
        const duracao = p.duracaoEmMinutos || 0;
        if (duracao < filtros.duracaoRange[0] || duracao > filtros.duracaoRange[1]) {
          return false;
        }
      }

      // 🌟 Filtro por reviews (≥ nota escolhida)
      if (filtros.reviews && filtros.reviews.length > 0) {
        const resumo = resumos[p.id!]; // { media, total }
        if (!resumo) return false;

        // pega o maior valor marcado → [3,4] = min 4
        const minReview = Math.max(...filtros.reviews);

        if (resumo.media < minReview) {
          return false;
        }
      }

      // 🎯 Diferenciais
      if (filtros.temDiferencial && (!p.diferenciais || p.diferenciais.length === 0)) {
        return false;
      }

      return true;
    });
  }, [pacotesOriginais, filtros, resumos]);

  useEffect(() => {
    setSelectedItems(new Array(pacotesFiltrados.length).fill(false));
  }, [pacotesFiltrados.length]);

  const handleSearch = async (valores: Filtros) => {
    try {
      // atualiza filtros localmente (opcional)
      setFiltros((prev) => ({ ...prev, ...valores }));

      // monta query params
      const query = new URLSearchParams();
      if (valores.termo) query.append("termo", valores.termo);
      if (valores.idioma) query.append("idioma", valores.idioma);
      if (valores.pessoas) query.append("pessoas", valores.pessoas.toString());
      if (valores.servicoId) query.append("servicoId", valores.servicoId.toString());
      if (valores.data) query.append("data", valores.data);
      if (valores.hora) query.append("hora", valores.hora);

      const res = await fetch(`${BASE_URL}/pacotes/search?${query.toString()}`);
      if (!res.ok) throw new Error("Erro ao buscar pacotes");
      const result: PacoteDTO[] = await res.json();
      console.log("Pacotes retornados:", result);
      setPacotesOriginais(result);
    } catch (err) {
      console.error("Erro na busca de pacotes:", err);
    }
  };

  const handleFilterChange = (novosFiltros: Partial<Filtros>) => {
    setFiltros((prev) => ({ ...prev, ...novosFiltros }));
  };

  const handleItemClick = (index: number) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  // Breadcrumbs
  const breadcrumbs = [
    { label: "Tours", link: routes.home1, active: false },
    { label: "Tuk Tuk", active: false },
    { label: "Tuk Tuk grid", active: true },
  ];

  // Slider helper
  const imgslideroption = (numFotos: number) => ({
    dots: true,
    arrows: true,
    infinite: numFotos > 1,
    speed: 500,
    autoplay: false,
    swipe: true,
    slidesToShow: 1,
    slidesToScroll: 1,
  });

  const getImgUrl = (url: string) => (url.startsWith("http") ? url : `${BASE_URL}${url}`);

  return (
    <>
      <Breadcrumb title="Tours" breadcrumbs={breadcrumbs} backgroundClass="breadcrumb-bg-05" />
      <div className="content">
        <div className="container">
          {/* Search topo */}
          <div className="mb-5">
            <FlightSearch
              mostrarTodosFiltros={true}
              servicos={[]}
              servicoSelecionado={filtros.servicoId}
              setServicoSelecionado={(id) => setFiltros((prev) => ({ ...prev, servicoId: id }))}
              onSearch={handleSearch}
            />
          </div>

          <div className="row">
            {/* Filtro lateral */}
            <FlightFilter onFilterChange={handleFilterChange} />

            <div className="col-xl-9 col-lg-9">
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                <h6>{pacotesFiltrados.length} Pacotes encontrados</h6>
              </div>

              <div className="row justify-content-start">
                {pacotesFiltrados.length === 0 ? (
                  <p>Nenhum pacote encontrado.</p>
                ) : (
                  pacotesFiltrados.map((pacote, index) => {
                    const fotos: string[] =
                      pacote.fotoUrls && pacote.fotoUrls.length > 0
                        ? pacote.fotoUrls
                        : ["assets/img/flight/flight-09.jpg"];

                    const sliderOptions = imgslideroption(fotos.length);

                    return (
                      <div className="col-xxl-4 col-md-6 d-flex" key={pacote.id || index}>
                        <div className="place-item common-grid-slider mb-4 flex-fill">
                          <div className="place-img">
                            <Slider {...sliderOptions} className="img-slider image-slide">
                              {fotos.map((url, i) => (
                                <div className="slide-images" key={i}>
                                  <Link to={`/flight-details/${pacote.id}`}>
                                    <img src={getImgUrl(url)} className="img-fluid" alt={pacote.titulo || "Pacote"} />
                                  </Link>
                                </div>
                              ))}
                            </Slider>

                            <div className="fav-item" onClick={() => handleItemClick(index)}>
                              <div className="d-flex align-items-center">
                                <Link to="#" className={`fav-icon ${selectedItems[index] ? "selected" : ""}`}>
                                  <i className="isax isax-heart5"></i>
                                </Link>
                              </div>
                            </div>
                          </div>

                          <div className="place-content">
                            <h5 className="text-truncate mb-1">
                              <Link to={`/flight-details/${pacote.id}`}>{pacote.titulo}</Link>
                            </h5>

                            {pacote.diferenciais && pacote.diferenciais.length > 0 && (
                              <div className="mb-2">
                                <span
                                  className="badge fs-10 fw-medium d-flex align-items-center"
                                  style={{
                                    backgroundColor: "#cfe2ff",
                                    color: "#0d6efd",
                                    padding: "0.25rem 0.5rem",
                                    borderRadius: "6px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <i className="isax isax-star5 me-1"></i>
                                  Has diferencial
                                </span>
                              </div>
                            )}

                            <div className="d-flex align-items-center justify-content-between border-top pt-3">
                              {/* Preço + Avaliação juntos */}
                              <div className="d-flex align-items-center">
                                <h6 className="text-primary mb-0 me-3">
                                  $ {pacote.precoBase?.toFixed(2)}
                                </h6>

                                {resumos[pacote.id!] ? (
                                  resumos[pacote.id!].total > 0 ? (
                                    <div className="d-flex align-items-center">
                                      <i className="isax isax-star1 text-warning me-1 fs-14" />
                                      <span className="badge badge-warning badge-xs text-gray-9 fs-10 fw-medium me-1 p-1">
                                        <i className="isax isax-star" />
                                        {resumos[pacote.id!].media.toFixed(1)}
                                      </span>
                                      <small className="text-muted">
                                        ({resumos[pacote.id!].total})
                                      </small>
                                    </div>
                                  ) : (
                                    <small className="text-muted">No reviews</small>
                                  )
                                ) : null}
                              </div>

                              {/* Idiomas no lado oposto */}
                              <div className="d-flex align-items-center flex-wrap">
                                {pacote.idiomas && pacote.idiomas.length > 0 && (
                                  <>
                                    {pacote.idiomas.slice(0, 2).map((idioma, i) => (
                                      <span
                                        key={i}
                                        className="badge bg-outline-success fs-10 fw-medium me-2 mb-1"
                                      >
                                        {idioma}
                                      </span>
                                    ))}
                                    {pacote.idiomas.length > 2 && (
                                      <span className="badge bg-outline-success fs-10 fw-medium mb-1">
                                        +{pacote.idiomas.length - 2} more
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlightGrid;